import { LoadingIndicator, PaginationPageDefault } from "@components";
import { Button } from "@components/button/button-untitledui";
import { dayjs } from "@lib";
import { Suspense, useState } from "react";
import {
	ExpenseCostFilters,
	ExpensesImportModal,
	ExpensesPageHeader,
	ExpensesSummaryCards,
	NewExpenseModal,
	VehicleExpenseCard,
} from "../components";
import { useGetExpensesRanking } from "../hooks";
import type { ExpenseStatus } from "../types";

const PAGE_SIZE = 12;

const MESSAGE_PANEL_CLASS =
	"rounded-xl border border-border-secondary bg-primary p-8 text-center text-secondary text-sm";

function ExpensesPageContent() {
	const [statusFilter, setStatusFilter] = useState<ExpenseStatus | 0>(0);
	const [currentPage, setCurrentPage] = useState(1);

	const {
		data: rankingData,
		isFetching,
		isError,
		refetch,
	} = useGetExpensesRanking({
		status: statusFilter,
		page: currentPage,
		pageSize: PAGE_SIZE,
	});

	const overview = rankingData?.header;
	const items = rankingData?.vehicles.items ?? [];
	const totalPages = rankingData?.vehicles.totalPages ?? 1;
	const hasItems = items.length > 0;
	const isInitialLoad =
		isFetching && !hasItems && (overview?.totalVehicles ?? 0) === 0;
	const [isNewExpenseModalOpen, setIsNewExpenseModalOpen] = useState(false);
	const [isImportModalOpen, setIsImportModalOpen] = useState(false);

	const subtitle =
		overview && !isInitialLoad
			? `${dayjs()
					.month(overview.month - 1)
					.format(
						"MMMM"
					)} ${overview.year} · ${overview.totalVehicles} veículos`
			: "Carregando...";

	const renderVehicles = () => {
		if (isInitialLoad) {
			return (
				<output className="flex min-h-[200px] items-center justify-center">
					<LoadingIndicator label="Carregando custos por veículo" size="md" />
				</output>
			);
		}

		if (isError && !hasItems) {
			return (
				<div
					className={`${MESSAGE_PANEL_CLASS} flex flex-col items-center gap-3`}
					role="alert"
				>
					<p>Não foi possível carregar os custos por veículo.</p>
					<Button
						color="secondary"
						onClick={() => {
							refetch();
						}}
						size="sm"
					>
						Tentar novamente
					</Button>
				</div>
			);
		}

		if (!hasItems) {
			return (
				<div className={MESSAGE_PANEL_CLASS}>
					Nenhum veículo encontrado para os filtros selecionados.
				</div>
			);
		}

		return (
			<>
				<div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
					{items.map((item) => (
						<VehicleExpenseCard item={item} key={item.vehicleId} />
					))}
				</div>
				{totalPages > 1 && (
					<div className="flex justify-center">
						<PaginationPageDefault
							onPageChange={setCurrentPage}
							page={currentPage}
							total={totalPages}
						/>
					</div>
				)}
			</>
		);
	};

	return (
		<div className="w-full space-y-6 px-6 py-8">
			<ExpensesPageHeader
				onImport={() => {
					setIsImportModalOpen(true);
				}}
				onNewExpense={() => {
					setIsNewExpenseModalOpen(true);
				}}
				subtitle={subtitle}
			/>

			<NewExpenseModal
				isOpen={isNewExpenseModalOpen}
				onClose={() => {
					setIsNewExpenseModalOpen(false);
				}}
				onSuccess={() => {
					setIsNewExpenseModalOpen(false);
				}}
			/>

			<ExpensesImportModal
				isOpen={isImportModalOpen}
				onClose={() => {
					setIsImportModalOpen(false);
				}}
			/>

			<ExpensesSummaryCards data={overview} isLoading={isInitialLoad} />

			<section
				aria-labelledby="custo-por-veiculo-heading"
				className="space-y-4"
			>
				<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
					<h2
						className="font-semibold text-lg text-primary leading-7"
						id="custo-por-veiculo-heading"
					>
						Custo por veículo
					</h2>

					<ExpenseCostFilters
						onChange={(v) => {
							setStatusFilter(v);
							setCurrentPage(1);
						}}
						value={statusFilter}
					/>
				</div>

				{renderVehicles()}
			</section>
		</div>
	);
}

export default function ExpensesPage() {
	return (
		<Suspense
			fallback={<div className="p-6 text-secondary text-sm">Carregando...</div>}
		>
			<ExpensesPageContent />
		</Suspense>
	);
}
